import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Hop as Home, Calendar, Building, Image, Users as UsersIcon, Bell, LogOut, Menu, X, Mic, History, BookOpen, Heart, MessageCircle, Settings } from 'lucide-react';

const AdminLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const navigation = [
    { name: 'الرئيسية', href: '/', icon: Home },
    { name: 'الأحداث', href: '/events', icon: Calendar },
    { name: 'القاعات', href: '/halls', icon: Building },
    { name: 'الحجوزات', href: '/bookings', icon: Calendar },
    { name: 'المستخدمين', href: '/users', icon: UsersIcon },
    { name: 'المعرض', href: '/gallery', icon: Image },
    { name: 'الأحاديث', href: '/hadith', icon: BookOpen },
    { name: 'الأذكار', href: '/azkar', icon: Heart },
    { name: 'الأدعية', href: '/duas', icon: MessageCircle },
    { name: 'الخطب', href: '/speeches', icon: Mic },
    { name: 'إرسال إشعار', href: '/notifications', icon: Bell },
    { name: 'سجل الإشعارات', href: '/notifications/history', icon: History },
    { name: 'إعدادات التطبيق', href: '/app-settings', icon: Settings },
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-primary-dark">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
        <div className="fixed right-0 top-0 h-full w-64 bg-card shadow-xl">
          <div className="flex items-center justify-between p-4 border-b border-divider">
            <h2 className="text-lg font-semibold text-accent-gold">لوحة التحكم</h2>
            <button
              onClick={() => setSidebarOpen(false)}
              className="p-2 hover:bg-primary-light rounded-lg"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <nav className="p-4 space-y-2">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center space-x-3 space-x-reverse px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  location.pathname === item.href
                    ? 'bg-accent-gold text-primary-dark'
                    : 'text-text-secondary hover:text-text-primary hover:bg-primary-light'
                }`}
              >
                <item.icon className="h-5 w-5" />
                <span>{item.name}</span>
              </Link>
            ))}
          </nav>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:right-0 lg:w-64 lg:block">
        <div className="flex flex-col h-full bg-card border-l border-divider">
          <div className="flex items-center px-6 py-4 border-b border-divider">
            <h1 className="text-xl font-bold text-accent-gold">مسجد الحنان والمنان</h1>
          </div>
          
          <nav className="flex-1 p-4 space-y-2">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`flex items-center space-x-3 space-x-reverse px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  location.pathname === item.href
                    ? 'bg-accent-gold text-primary-dark'
                    : 'text-text-secondary hover:text-text-primary hover:bg-primary-light'
                }`}
              >
                <item.icon className="h-5 w-5" />
                <span>{item.name}</span>
              </Link>
            ))}
          </nav>

          <div className="p-4 border-t border-divider">
            <div className="flex items-center space-x-3 space-x-reverse mb-4">
              <div className="h-8 w-8 bg-accent-gold rounded-full flex items-center justify-center">
                <span className="text-sm font-medium text-primary-dark">
                  {user?.name?.[0] || 'A'}
                </span>
              </div>
              <div>
                <p className="text-sm font-medium text-text-primary">{user?.name}</p>
                <p className="text-xs text-text-muted">{user?.email}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center space-x-2 space-x-reverse w-full px-3 py-2 text-sm text-text-secondary hover:text-text-primary hover:bg-primary-light rounded-lg transition-colors"
            >
              <LogOut className="h-4 w-4" />
              <span>تسجيل الخروج</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:mr-64">
        {/* Top bar */}
        <div className="sticky top-0 z-40 bg-primary-dark border-b border-divider px-4 py-3 lg:px-6">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 hover:bg-primary-light rounded-lg lg:hidden"
            >
              <Menu className="h-6 w-6" />
            </button>
            
            <div className="hidden lg:block">
              <h2 className="text-lg font-semibold text-text-primary">لوحة التحكم</h2>
            </div>

            <div className="flex items-center space-x-4 space-x-reverse">
              <div className="text-sm text-text-secondary">
                مرحباً، {user?.name}
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="p-4 lg:p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;